import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  pickLang,
  type ContestState,
  type EventTexts,
  type Game,
  type MissionResult,
} from "@shared";
import { currentLang } from "../i18n";
import ContestCard from "./ContestCard";
import { formatDuration } from "./gameApi";

interface Props {
  texts: EventTexts;
  /** Dernier résultat de chaque jeu. Une entrée = le jeu a déjà été terminé. */
  results: Partial<Record<Game, MissionResult>>;
  busy: boolean;
  /** Faux quand aucun objet n'a été préparé : la mission 1 serait vide. */
  mission1Available: boolean;
  /** Concours de fin : désactivé, verrouillé, ouvert ou déjà joué. */
  contest: ContestState;
  onStart: (game: Game) => void;
  onRereadWelcome: () => void;
  /** Recharge l'état après une participation, pour afficher le remerciement. */
  onContestEntered: () => void;
}

/**
 * Accueil : le point d'entrée des deux missions.
 *
 * Quatre boutons — un « plan de mission » et un « départ » par expédition —
 * comme demandé. Le plan se déplie sur place plutôt que d'ouvrir une fenêtre :
 * sur un téléphone tenu par un jeune, un panneau qui s'ouvre et qu'il faut
 * refermer est une occasion de se perdre.
 */
export default function HomeScreen({
  texts,
  results,
  busy,
  mission1Available,
  contest,
  onStart,
  onRereadWelcome,
  onContestEntered,
}: Props) {
  const { t } = useTranslation();
  const lang = currentLang();
  const [open, setOpen] = useState<1 | 2 | null>(null);

  const label = (game: Game) => (results[game] !== undefined ? t("home.replay") : t("home.start"));

  return (
    <>
      <div className="missions">
        <MissionCard
          number={1}
          title={pickLang(texts.mission1_title, lang)}
          body={pickLang(texts.mission1_body, lang)}
          badge="🔎"
          expanded={open === 1}
          result={results.objects}
          onToggle={() => {
            setOpen(open === 1 ? null : 1);
          }}
          action={
            <button
              type="button"
              className="button button--primary button--full button--big"
              disabled={busy || !mission1Available}
              onClick={() => {
                onStart("objects");
              }}
            >
              {label("objects")}
            </button>
          }
        />

        <MissionCard
          number={2}
          title={pickLang(texts.mission2_title, lang)}
          body={pickLang(texts.mission2_body, lang)}
          badge="🪐"
          expanded={open === 2}
          result={results.pairing}
          onToggle={() => {
            setOpen(open === 2 ? null : 2);
          }}
          action={
            <button
              type="button"
              className="button button--primary button--full button--big"
              disabled={busy}
              onClick={() => {
                onStart("pairing");
              }}
            >
              {label("pairing")}
            </button>
          }
        />
      </div>

      {/* Sous les deux missions : le concours se mérite, il vient donc après. */}
      <ContestCard texts={texts} contest={contest} onEntered={onContestEntered} />

      <button type="button" className="button button--ghost button--small" onClick={onRereadWelcome}>
        {t("welcome.reread")}
      </button>
    </>
  );
}

function MissionCard({
  number,
  title,
  body,
  badge,
  expanded,
  result,
  onToggle,
  action,
}: {
  number: 1 | 2;
  title: string;
  body: string;
  badge: string;
  expanded: boolean;
  result: MissionResult | undefined;
  onToggle: () => void;
  action: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [showResult, setShowResult] = useState(false);
  const bodyId = `mission-${number}-body`;
  const resultId = `mission-${number}-result`;

  return (
    <section className="mission-card">
      <p className="mission-card__badge">{badge}</p>
      <h2 className="mission-card__title">{title}</h2>

      <button
        type="button"
        className="button button--ghost button--full"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        {expanded ? t("home.hidePlan") : t("home.showPlan")}
      </button>

      {expanded && (
        <p id={bodyId} className="mission-card__body">
          {body}
        </p>
      )}

      {/* N'apparaît qu'une fois la mission terminée au moins une fois : un
          bouton « résultat » avant d'avoir joué n'aurait rien à montrer. */}
      {result !== undefined && (
        <>
          {/* Le badge vert ne se décerne qu'au SANS-FAUTE. Avant, terminer
              suffisait : on pouvait afficher « complétée » avec trois objets
              sur dix. Une expédition jouée sans le 100 % garde un bouton, mais
              sobre — le jeune doit pouvoir revoir son résultat sans qu'on lui
              annonce une réussite qu'il n'a pas. */}
          {/* Deux lignes : le VERDICT, qui porte la couleur, et l'invitation à
              voir le détail, plus discrète. Le même bouton dans les deux cas —
              seule la couleur dit l'issue, et le résultat reste consultable
              même quand l'expédition n'est pas complète. */}
          <button
            type="button"
            className={`button button--full button--verdict ${
              result.completed ? "button--done" : "button--missed"
            }`}
            aria-expanded={showResult}
            aria-controls={resultId}
            onClick={() => {
              setShowResult(!showResult);
            }}
          >
            <span>{result.completed ? t("home.completed") : t("home.incomplete")}</span>
            <span className="button__sub">{t("home.seeResult")}</span>
          </button>

          {showResult && (
            <div
              id={resultId}
              className={`mission-card__result ${
                result.completed ? "" : "mission-card__result--missed"
              }`}
            >
              <p className="mission-card__percent">{result.percent} %</p>
              <p className="mission-card__stats">
                {t("game.summary.found", { count: result.found, total: result.total })}
                {/* Les erreurs se disent à part : celui qui a trouvé les dix
                    vrais objets a bien tout trouvé, ce sont ses coches en trop
                    qui l'empêchent d'être complet. */}
                {result.wrong > 0 && (
                  <>
                    <br />
                    {t("game.summary.wrong", { count: result.wrong })}
                  </>
                )}
                <br />
                {t("game.summary.duration")} <strong>{formatDuration(result.durationSeconds)}</strong>
              </p>
            </div>
          )}
        </>
      )}

      {action}
    </section>
  );
}
